import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Select from "@/components/Select";
import SectionHeading from "@/components/SectionHeading";
import SkeletonElement from "@/components/Skeleton/SkeletonElement";
import TextField from "@/components/TextField";
import usePageTitle from "@/hooks/usePageTitle";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider/useErrorBanner";
import TabLayout from "../_TabLayout";
import ConfigurationLoadError from "../_components/ConfigurationLoadError";
import { useBirthdayGreetingConfigurationAccess } from "../access";
import { shouldSwitchConfigurationToLocalBackend } from "../localBackend";
import {
  DEFAULT_BIRTHDAY_GREETING_SUBJECT,
  MESSAGE_TEMPLATE_TYPE,
  MESSAGE_TEMPLATE_TYPE_OPTIONS,
  type MessageTemplateType
} from "../messageTemplateTypes";
import {
  MESSAGE_TEMPLATE_QUERY,
  type MessageTemplateQueryResponse,
  type MessageTemplateQueryVariables
} from "../queries";
import {
  type UpsertMessageTemplateResponse,
  type UpsertMessageTemplateVariables,
  UPSERT_MESSAGE_TEMPLATE_MUTATION
} from "../mutations";

const MAX_TEMPLATE_TEXT_LENGTH = 2000;
const MAX_SUBJECT_LENGTH = 200;
export const INCOMPLETE_PERSONALIZATION_DESCRIPTION =
  "Your custom copy is not used until both subject and message are filled.";

function mutationErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export default function BirthdayGreetingsPage() {
  usePageTitle("/configuration/birthday-greetings", "Member 360 Configuration | Birthday Greetings");
  const { loaded, canView } = useBirthdayGreetingConfigurationAccess();
  const { setError } = useErrorBanner();
  const [templateType, setTemplateType] = useState<MessageTemplateType>(MESSAGE_TEMPLATE_TYPE.BIRTHDAY_GREETING);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [signature, setSignature] = useState("");
  const skip = !loaded || !canView || shouldSwitchConfigurationToLocalBackend();

  const { data, loading, error, refetch } = useQuery<MessageTemplateQueryResponse, MessageTemplateQueryVariables>(
    MESSAGE_TEMPLATE_QUERY,
    {
      skip,
      variables: { templateType }
    }
  );
  const [upsertMessageTemplate, { loading: saving }] = useMutation<
    UpsertMessageTemplateResponse,
    UpsertMessageTemplateVariables
  >(UPSERT_MESSAGE_TEMPLATE_MUTATION, {
    refetchQueries: [MESSAGE_TEMPLATE_QUERY],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    setSubject(data?.messageTemplate?.subject ?? "");
    setBody(data?.messageTemplate?.body ?? "");
    setSignature(data?.messageTemplate?.signature ?? "");
  }, [data, templateType]);

  const subjectTooLong = subject.length > MAX_SUBJECT_LENGTH;
  const bodyTooLong = body.length > MAX_TEMPLATE_TEXT_LENGTH;
  const signatureTooLong = signature.length > MAX_TEMPLATE_TEXT_LENGTH;
  const canSave = !loading && !saving && !subjectTooLong && !bodyTooLong && !signatureTooLong;
  const hasSubject = subject.trim().length > 0;
  const hasBody = body.trim().length > 0;
  const incompletePersonalization = hasSubject !== hasBody;

  const handleSave = async () => {
    try {
      const result = await upsertMessageTemplate({
        variables: {
          input: {
            templateType,
            subject,
            body,
            signature
          }
        }
      });
      const payload = result.data?.upsertMessageTemplate;
      if (payload?.success) {
        showSnackbar(payload.message);
        return;
      }
      setError(payload?.message || "Couldn’t save your birthday message.");
    } catch (caught) {
      setError(mutationErrorMessage(caught, "Couldn’t save your birthday message."));
    }
  };

  return (
    <TabLayout>
      <SectionHeading
        heading="Birthday Greetings"
        body="This is your birthday message. PGA Career Services sends it to members on their birthday."
      />
      {error ? (
        <ConfigurationLoadError onRetry={() => refetch()} />
      ) : loading || skip ? (
        <div className="flex max-w-[640px] flex-col gap-6" data-testid="birthday-greeting-template-skeleton">
          <SkeletonElement height="h-12" width="w-full" />
          <SkeletonElement height="h-12" width="w-full" />
          <SkeletonElement height="h-48" width="w-full" />
          <SkeletonElement height="h-24" width="w-full" />
          <SkeletonElement height="h-10" width="w-20" />
        </div>
      ) : (
        <form
          className="flex max-w-[640px] flex-col gap-6"
          data-testid="birthday-greeting-template-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSave) {
              void handleSave();
            }
          }}
        >
          <Select
            label="Template Type"
            value={templateType}
            options={MESSAGE_TEMPLATE_TYPE_OPTIONS}
            onSelect={(value) => setTemplateType(value as MessageTemplateType)}
            disabled={loading || saving}
            data-testid="message-template-type-select"
          />
          <TextField
            label="Email Subject"
            value={subject}
            onChangeText={setSubject}
            disabled={loading || saving}
            supportingText={`${subject.length}/${MAX_SUBJECT_LENGTH} characters. Leave blank to use "${DEFAULT_BIRTHDAY_GREETING_SUBJECT}". Fill in both subject and message for your personalized email on dual-coverage sections.`}
            error={subjectTooLong}
            errorText={`Keep the subject to ${MAX_SUBJECT_LENGTH} characters or fewer.`}
          />
          <TextField
            label="Birthday Message"
            type="textarea"
            value={body}
            onChangeText={setBody}
            rows={8}
            disabled={loading || saving}
            supportingText={`${body.length}/${MAX_TEMPLATE_TEXT_LENGTH} characters. Leave this blank to use the approved default.`}
            error={bodyTooLong}
            errorText={`Keep the message to ${MAX_TEMPLATE_TEXT_LENGTH} characters or fewer.`}
          />
          {incompletePersonalization ? (
            <Banner
              variant="warn"
              description={INCOMPLETE_PERSONALIZATION_DESCRIPTION}
              hideSecondaryDismiss
              inline
              testId="birthday-greeting-incomplete-personalization"
            />
          ) : null}
          <TextField
            label="Signature"
            type="textarea"
            value={signature}
            onChangeText={setSignature}
            rows={4}
            disabled={loading || saving}
            supportingText={`${signature.length}/${MAX_TEMPLATE_TEXT_LENGTH} characters. This extra text appears above your name and title.`}
            error={signatureTooLong}
            errorText={`Keep the signature to ${MAX_TEMPLATE_TEXT_LENGTH} characters or fewer.`}
          />
          <div>
            <Button variant="filled" type="submit" disabled={!canSave} loading={saving} testId="birthday-greeting-save">
              Save
            </Button>
          </div>
        </form>
      )}
    </TabLayout>
  );
}
